import type { MeasurementRow, MeasurementTable, MeasurementWorkbook } from "@shared/measurement";
import type { WorkBook, WorkSheet } from "xlsx";
import { parseMeasurementQuantity } from "@shared/measurement-quantity";
export { parseMeasurementQuantity } from "@shared/measurement-quantity";

const MAX_ROWS = 5000;
const MAX_COLUMNS = 100;
const MAX_SHEETS = 30;

const headerKey = (value: unknown) => String(value ?? "").normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
const text = (value: unknown) => String(value ?? "").trim().slice(0, 4000);

function findHeader(rows: unknown[][]) {
  const aliases = {
    code: ["lvpos", "lvposition", "position", "positionsnummer", "pos", "posnr", "oz", "code"],
    name: ["kurztext", "bezeichnung", "leistung", "leistungsbezeichnung", "beschreibung", "text"],
    unit: ["eh", "me", "einheit", "mengeneinheit"],
    quantity: ["menge", "gesamtmenge", "anzahl", "quantity"],
    note: ["grundlagebemerkung", "grundlage", "bemerkung", "hinweis", "anmerkung"],
  };
  for (let index = 0; index < Math.min(30, rows.length); index++) {
    const headers = rows[index].map(headerKey);
    const columns = Object.fromEntries(Object.entries(aliases).map(([key, names]) =>
      [key, headers.findIndex((value) => names.includes(value))])) as Record<keyof typeof aliases, number>;
    if (columns.unit >= 0 && columns.quantity >= 0 && (columns.code >= 0 || columns.name >= 0)) {
      return { index, columns };
    }
  }
  return null;
}

/** Read the prepared summary only, not the geometry/length detail sheets.
 * Formula results must be cached in the workbook; formulas are never executed. */
export async function parseMeasurementFile(file: File): Promise<MeasurementWorkbook> {
  if (!/\.(xlsx|xls|csv|pdf)$/i.test(file.name)) {
    throw new Error("Bitte eine Excel-Datei (.xlsx/.xls), CSV oder PDF-Maßaufstellung auswählen.");
  }
  if (file.size > 8 * 1024 * 1024) throw new Error("Die Maßaufstellung darf höchstens 8 MB groß sein.");
  if (/\.pdf$/i.test(file.name)) {
    const body = new FormData();
    body.append("file", file);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 35000);
    try {
      const response = await fetch("/api/measurements/parse-pdf", {
        method: "POST", body, credentials: "include", signal: controller.signal,
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "PDF-Maßaufstellung konnte nicht gelesen werden.");
      return result;
    } catch (error) {
      if (controller.signal.aborted) throw new Error("PDF-Import hat zu lange gedauert. Bitte eine kleinere Zusammenstellung verwenden.");
      throw error;
    } finally { clearTimeout(timeout); }
  }
  const XLSX = await import("xlsx");
  let workbook: WorkBook;
  try {
    workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: true, sheetRows: MAX_ROWS + 1, cellFormula: true });
  } catch {
    throw new Error("Die Datei konnte nicht als Excel-/CSV-Maßaufstellung gelesen werden.");
  }
  if (workbook.SheetNames.length > MAX_SHEETS) throw new Error("Die Datei enthält zu viele Tabellenblätter (maximal 30).");
  const tables: MeasurementTable[] = [];
  const warnings: string[] = [];
  for (const sheetName of workbook.SheetNames) {
    const sheet: WorkSheet = workbook.Sheets[sheetName];
    if (!sheet["!ref"]) continue;
    const range = XLSX.utils.decode_range(sheet["!fullref"] ?? sheet["!ref"]);
    if (range.e.r >= MAX_ROWS || range.e.c >= MAX_COLUMNS) {
      throw new Error(`Das Blatt „${sheetName}“ ist zu groß (maximal ${MAX_ROWS} Zeilen und ${MAX_COLUMNS} Spalten). Bitte eine kleinere Zusammenstellung exportieren.`);
    }
    const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: null, range: 0, blankrows: true });
    const header = findHeader(rows);
    if (!header) {
      if (/annahm|offen|hinweis|anmerk|grundlag/i.test(sheetName)) {
        const notes = rows.map((row) => row.map(text).filter(Boolean).join(" · ")).filter(Boolean);
        warnings.push(...notes.slice(0, 100).map((note) => `${sheetName}: ${note}`));
      }
      continue;
    }
    warnings.push(...rows.slice(0, header.index).map((row) => row.map(text).filter(Boolean).join(" · ")).filter(Boolean));
    const parsed: MeasurementRow[] = [];
    for (let index = header.index + 1; index < rows.length; index++) {
      const row = rows[index];
      const get = (column: number) => column >= 0 ? row[column] : undefined;
      const { columns } = header;
      const codeCell = columns.code >= 0 ? sheet[XLSX.utils.encode_cell({ r: index, c: columns.code })] : undefined;
      const code = text(typeof codeCell?.v === "number" && /^\d+$/.test(codeCell.w ?? "") ? codeCell.w : get(columns.code));
      const name = text(get(columns.name));
      const unit = text(get(columns.unit));
      // With an LV-code column, the unlabeled figures after the table are
      // reference totals, not additional positions.
      if (columns.code >= 0 ? !code : !name) continue;
      if (!unit && get(columns.quantity) == null) continue;
      const cell = sheet[XLSX.utils.encode_cell({ r: index, c: columns.quantity })];
      const missingFormulaResult = !!cell?.f && (cell.v === undefined || cell.v === null || cell.v === "");
      const formulaError = cell?.t === "e";
      const quantity = missingFormulaResult || formulaError ? null : parseMeasurementQuantity(get(columns.quantity));
      const issues: string[] = [];
      if (!unit) issues.push("Einheit fehlt.");
      if (quantity === null) issues.push(formulaError ? "Die Mengenzelle enthält einen Excel-Formelfehler." : missingFormulaResult
        ? "Die Formel hat kein gespeichertes Ergebnis. Bitte in Excel neu berechnen und speichern."
        : "Menge fehlt oder ist keine gültige nichtnegative Zahl.");
      parsed.push({
        id: `${sheetName}:${index + 1}`, rowNumber: index + 1,
        code, name: name || code, unit, quantity, note: text(get(columns.note)), issues,
      });
    }
    if (parsed.length) tables.push({ sheet: sheetName, rows: parsed });
  }
  if (!tables.length) {
    throw new Error("Keine Mengentabelle gefunden. Benötigte Spalten: LV-Pos. oder Bezeichnung, EH/Einheit und Menge.");
  }
  return { fileName: file.name, tables, warnings: Array.from(new Set(warnings)) };
}
